import { useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

const DAYS = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];
const FIELD_LABELS = {
  prenom: 'Prénom', nom: 'Nom', telephone: 'Téléphone', email: 'Email',
  code_postal: 'Code postal', adresse: 'Adresse', entreprise: 'Entreprise', message: 'Message',
};
const COLORS = ['#004AAD', '#059669', '#EA580C', '#9333EA', '#DC2626', '#0891B2'];

export const emptyLink = () => ({
  title: '', slug: '', description: '', event_type: 'rdv_client', duration_min: 30,
  location: '', min_notice_hours: 24, days_ahead: 14, assigned_user_ids: [],
  availability_scope: 'all', color: '#004AAD', notify_emails: [], active: true,
  hours: DAYS.map((_, d) => ({ day: d, enabled: d < 5, start: '09:00', end: '18:00' })),
  fields: Object.keys(FIELD_LABELS).map((k) => ({
    key: k,
    enabled: ['prenom', 'nom', 'telephone', 'email', 'message'].includes(k),
    required: ['nom', 'telephone'].includes(k),
  })),
});

const Lbl = ({ children }) => (
  <label className="text-xs font-medium text-slate-600 block mb-1">{children}</label>
);

const Section = ({ title, children }) => (
  <div className="border border-slate-200 rounded-xl p-4 space-y-3">
    <div className="text-sm font-semibold text-slate-900">{title}</div>
    {children}
  </div>
);

function HoursEditor({ hours, onChange }) {
  const upd = (i, patch) => onChange(hours.map((h, j) => (j === i ? { ...h, ...patch } : h)));
  return (
    <div className="space-y-2">
      {hours.map((h, i) => (
        <div key={h.day} className="flex items-center gap-3" data-testid={`booking-form-day-${h.day}`}>
          <Switch checked={h.enabled} onCheckedChange={(v) => upd(i, { enabled: v })}
                  data-testid={`booking-form-day-${h.day}-toggle`} />
          <span className={`w-24 text-sm ${h.enabled ? 'text-slate-900' : 'text-slate-400'}`}>{DAYS[h.day]}</span>
          <Input type="time" value={h.start} disabled={!h.enabled} className="w-28 h-8"
                 onChange={(e) => upd(i, { start: e.target.value })}
                 data-testid={`booking-form-day-${h.day}-start`} />
          <span className="text-slate-400 text-xs">→</span>
          <Input type="time" value={h.end} disabled={!h.enabled} className="w-28 h-8"
                 onChange={(e) => upd(i, { end: e.target.value })}
                 data-testid={`booking-form-day-${h.day}-end`} />
        </div>
      ))}
    </div>
  );
}

function FieldsEditor({ fields, onChange }) {
  const upd = (i, patch) => onChange(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  return (
    <div className="grid sm:grid-cols-2 gap-2">
      {fields.map((f, i) => (
        <div key={f.key} className="flex items-center justify-between gap-2 border border-slate-100 rounded-lg px-3 py-2">
          <div className="flex items-center gap-2">
            <Switch checked={f.enabled} data-testid={`booking-form-field-${f.key}-toggle`}
                    onCheckedChange={(v) => upd(i, { enabled: v, required: v ? f.required : false })} />
            <span className="text-sm">{FIELD_LABELS[f.key]}</span>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-slate-500">
            <Checkbox checked={f.required} disabled={!f.enabled}
                      onCheckedChange={(v) => upd(i, { required: !!v })}
                      data-testid={`booking-form-field-${f.key}-required`} />
            Obligatoire
          </label>
        </div>
      ))}
    </div>
  );
}

function UsersPicker({ usersList, value, onChange }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  if (!usersList.length) return <p className="text-xs text-slate-400 italic">Aucun utilisateur</p>;
  return (
    <div className="flex flex-wrap gap-2">
      {usersList.map((u) => (
        <button key={u.user_id} type="button" onClick={() => toggle(u.user_id)}
                data-testid={`booking-form-user-${u.user_id}`}
                className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${value.includes(u.user_id) ? 'bg-blue-50 border-blue-300 text-blue-800 font-semibold' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
          {u.name || u.email}
        </button>
      ))}
    </div>
  );
}

export function BookingLinkForm({ initial, meta, usersList, onBack, onSaved }) {
  const [f, setF] = useState(initial);
  const [notify, setNotify] = useState((initial.notify_emails || []).join(', '));
  const [saving, setSaving] = useState(false);
  const set = (patch) => setF((p) => ({ ...p, ...patch }));

  const save = async () => {
    if (!f.title.trim()) { toast.error('Le titre est requis'); return; }
    setSaving(true);
    const payload = {
      ...f,
      slug: f.slug?.trim() || null,
      min_notice_hours: Number(f.min_notice_hours) || 0,
      days_ahead: Number(f.days_ahead) || 14,
      notify_emails: notify.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean),
    };
    try {
      if (f.link_id) await api.put(`/agenda/booking-links/${f.link_id}`, payload);
      else await api.post('/agenda/booking-links', payload);
      toast.success('Lien de réservation enregistré');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString() || 'Erreur enregistrement');
    } finally { setSaving(false); }
  };

  return (
    <div className="space-y-4" data-testid="booking-link-form">
      <button onClick={onBack} className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
              data-testid="booking-form-back-btn">
        <ArrowLeft className="w-3.5 h-3.5" /> Retour aux liens
      </button>

      <Section title="Informations">
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Lbl>Titre *</Lbl>
            <Input value={f.title} onChange={(e) => set({ title: e.target.value })}
                   placeholder="Visite technique gratuite" data-testid="booking-form-title" /></div>
          <div><Lbl>Adresse du lien (optionnel)</Lbl>
            <Input value={f.slug || ''} onChange={(e) => set({ slug: e.target.value })}
                   placeholder="visite-technique" data-testid="booking-form-slug" /></div>
        </div>
        <div><Lbl>Description affichée au visiteur</Lbl>
          <Textarea rows={2} value={f.description} onChange={(e) => set({ description: e.target.value })}
                    data-testid="booking-form-description" /></div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><Lbl>Type d'événement</Lbl>
            <Select value={f.event_type} onValueChange={(v) => set({ event_type: v })}>
              <SelectTrigger data-testid="booking-form-event-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(meta.event_types || []).map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}
              </SelectContent>
            </Select></div>
          <div><Lbl>Durée du créneau</Lbl>
            <Select value={String(f.duration_min)} onValueChange={(v) => set({ duration_min: Number(v) })}>
              <SelectTrigger data-testid="booking-form-duration"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[15, 30, 45, 60].map((d) => <SelectItem key={d} value={String(d)}>{d} minutes</SelectItem>)}
              </SelectContent>
            </Select></div>
          <div><Lbl>Lieu</Lbl>
            <Input value={f.location} onChange={(e) => set({ location: e.target.value })}
                   placeholder="Téléphone, visio, à domicile…" data-testid="booking-form-location" /></div>
        </div>
        <div><Lbl>Couleur</Lbl>
          <div className="flex items-center gap-2">
            {COLORS.map((c) => (
              <button key={c} type="button" onClick={() => set({ color: c })}
                      data-testid={`booking-form-color-${c.slice(1)}`}
                      className={`w-7 h-7 rounded-full border-2 transition-transform hover:scale-110 ${f.color === c ? 'border-slate-900' : 'border-white shadow'}`}
                      style={{ background: c }} />
            ))}
            <input type="color" value={f.color} onChange={(e) => set({ color: e.target.value })}
                   className="w-8 h-8 cursor-pointer" data-testid="booking-form-color-custom" />
          </div></div>
      </Section>

      <Section title="Disponibilités">
        <HoursEditor hours={f.hours} onChange={(hours) => set({ hours })} />
        <div className="grid sm:grid-cols-3 gap-3 pt-2">
          <div><Lbl>Délai minimum avant RDV (heures)</Lbl>
            <Input type="number" min={0} value={f.min_notice_hours}
                   onChange={(e) => set({ min_notice_hours: e.target.value })} data-testid="booking-form-min-notice" /></div>
          <div><Lbl>Nombre de jours proposés</Lbl>
            <Input type="number" min={1} max={60} value={f.days_ahead}
                   onChange={(e) => set({ days_ahead: e.target.value })} data-testid="booking-form-days-ahead" /></div>
          <div><Lbl>Créneaux bloqués par</Lbl>
            <Select value={f.availability_scope} onValueChange={(v) => set({ availability_scope: v })}>
              <SelectTrigger data-testid="booking-form-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les événements de l'Agenda</SelectItem>
                <SelectItem value="assigned">Événements des utilisateurs assignés</SelectItem>
              </SelectContent>
            </Select></div>
        </div>
      </Section>

      <Section title="Équipe">
        <div><Lbl>Utilisateurs assignés aux RDV</Lbl>
          <UsersPicker usersList={usersList} value={f.assigned_user_ids}
                       onChange={(v) => set({ assigned_user_ids: v })} /></div>
        <div><Lbl>Emails à notifier en plus (séparés par des virgules)</Lbl>
          <Input value={notify} onChange={(e) => setNotify(e.target.value)}
                 placeholder="commercial@renoresolve.com" data-testid="booking-form-notify" /></div>
      </Section>

      <Section title="Champs demandés au visiteur">
        <FieldsEditor fields={f.fields} onChange={(fields) => set({ fields })} />
      </Section>

      <div className="flex items-center justify-between gap-3 pt-1">
        <label className="flex items-center gap-2 text-sm text-slate-700">
          <Switch checked={f.active} onCheckedChange={(v) => set({ active: v })} data-testid="booking-form-active" />
          Lien actif
        </label>
        <Button onClick={save} disabled={saving} className="bg-[#004AAD] hover:bg-[#003580]"
                data-testid="booking-form-save-btn">
          {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
